import React, { useMemo, useState } from 'react';
import styles from './EditNetSuiteWarehouseForm.module.scss';
import SimpleInput from '../../common/SimpleInput';
import Button from '../../common/Button';
import { useDispatch, useSelector } from 'react-redux';
import { updateWarehouseData } from '../../../store/actions/warehouses';
import _ from 'lodash';
import { InfoTooltip } from '../../common/InfoTooltip';
import { Note } from '../../common/Note';
import history from '../../../utils/history';

export function EditNetSuiteWarehouseForm({ warehouse }) {
  const dispatch = useDispatch();
  const submitting = useSelector((state) => state.warehouses.submitting);
  const {
    orders_integration_data: {
      connection_data: ordersConnectionData,
      custom_configurations: ordersCustomConfigurations,
    },
    inventory_integration_data: { connection_data: inventoryConnectionData },
  } = warehouse;

  const [form, setForm] = useState({
    currencyId: ordersConnectionData.currency_id || inventoryConnectionData.currency_id,
    defaultEmailAddress: ordersConnectionData.default_email || inventoryConnectionData.default_email,
    saleChannel: ordersConnectionData.sale_channel || inventoryConnectionData.sale_channel,
    variantIdentifierFieldName:
      ordersConnectionData.variant_identifier_field_name || inventoryConnectionData.variant_identifier_field_name,
    defaultClass: ordersCustomConfigurations.default_class,
  });

  const onChange = (e) => {
    setForm((prevForm) => ({
      ...prevForm,
      [e.target.name]: e.target.value,
    }));
  };

  const isDisabled = useMemo(() => submitting, [submitting]);

  const backToWarehousesPage = (e) => {
    history.push(`/suppliers/${warehouse.supplier_id}/admin/warehouses`);
  };

  const onSubmit = (e) => {
    const data = {
      orders_integration_data: _.merge({}, warehouse.orders_integration_data, {
        connection_data: {
          currency_id: form.currencyId,
          default_email: form.defaultEmailAddress,
          sale_channel: form.saleChannel,
          variant_identifier_field_name: form.variantIdentifierFieldName,
        },
        custom_configurations: {
          default_class: form.defaultClass,
        },
      }),
      inventory_integration_data: _.merge({}, warehouse.inventory_integration_data, {
        connection_data: {
          currency_id: form.currencyId,
          default_email: form.defaultEmailAddress,
          sale_channel: form.saleChannel,
          variant_identifier_field_name: form.variantIdentifierFieldName,
        },
      }),
    };

    dispatch(updateWarehouseData(warehouse.id, data));

    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <div className={styles.wrapper}>
      <h2>Warehouse: {warehouse.name}</h2>

      <form className={styles.form} onSubmit={onSubmit}>
        <div className={styles.body}>
          <div className={styles.formControl}>
            <label>
              Currency ID
              <span className={styles.mandatory}>*</span>
              <InfoTooltip
                wide
                content={
                  <>
                    <p>Enter the Internal ID of USD currency record.</p>
                    <p>
                      To find the <strong>Currency ID</strong> - Go to <strong>Lists</strong> &gt;{' '}
                      <strong>Accounting</strong> &gt; <strong>Currencies</strong>.
                    </p>
                    <p>Example: "1". </p>
                  </>
                }
              />
            </label>
            <SimpleInput value={form.currencyId} onChange={onChange} name="currencyId" focus={false} />
          </div>

          <div className={styles.formControl}>
            <label>
              Default Email Address
              <span className={styles.mandatory}>*</span>
              <InfoTooltip
                wide
                content={
                  <>
                    <p>Enter your default Company Email Address.</p>
                    <p>Example: test@cym.bio</p>
                  </>
                }
              />
            </label>
            <SimpleInput
              value={form.defaultEmailAddress}
              onChange={onChange}
              name="defaultEmailAddress"
              focus={false}
            />
          </div>

          <div className={styles.formControl}>
            <label>
              Sales Channel
              <span className={styles.mandatory}>*</span>
              <InfoTooltip
                wide
                content={
                  <>
                    <p>Enter the Sales Channel.</p>
                    <p>
                      A Sales Channel is a category of sales transaction that you can differentiate by customer type and
                      distribution channel, such as web-shop or retail outlet.
                    </p>
                    <p>
                      For help working with this record in the UI, go to{' '}
                      <strong>Help Center - "Defining a Sales Channel Allocation"</strong>.
                    </p>
                    <p>Example: "Wholesale".</p>
                  </>
                }
              />
            </label>
            <SimpleInput value={form.saleChannel} onChange={onChange} name="saleChannel" focus={false} />
          </div>

          <div className={styles.formControl}>
            <label>
              Variant Identifier Field Name
              <span className={styles.mandatory}>*</span>
              <InfoTooltip
                wide
                content={
                  <>
                    <p>Enter the field name where the SKU value is stored.</p>
                    <p>Example: "itemId".</p>
                  </>
                }
              />
            </label>
            <SimpleInput
              value={form.variantIdentifierFieldName}
              onChange={onChange}
              name="variantIdentifierFieldName"
              focus={false}
            />
          </div>

          <div className={styles.formControl}>
            <label>
              Default Class
              <InfoTooltip
                wide
                content={
                  <>
                    <p>Enter a Default Class.</p>
                    <p>
                      Class Classes are categories that you can create to track records such as financials,
                      transactions, and employees. You set up a class for each account type and track the financial
                      performance of each class over any time period.
                    </p>
                    <p>Example: "Wholesale : Dropship (Cymbio)"</p>
                  </>
                }
              />
            </label>

            <SimpleInput value={form.defaultClass} onChange={onChange} name="defaultClass" focus={false} />
          </div>

          <Note>
            <strong>NOTE:</strong> You can find the Connection Guidelines on{' '}
            <a target="_blank" href="https://www.cym.bio/netsuite-integration/#integration-guide" rel="noreferrer">
              Cymbio Website
            </a>
            .
          </Note>
        </div>

        <div className={styles.footer}>
          <Button type="button" text="Cancel" loading={submitting} onClick={backToWarehousesPage} color="white" />
          <Button type="submit" text="Save" loading={submitting} disabled={isDisabled} />
        </div>
      </form>
    </div>
  );
}
